'use client';
import { Box, List, ListItem, Typography } from '@mui/material';
import { usePageAction } from '@repo/hooks/usePageAction';
import type { MonetizableEnum } from '@repo/supergraph/marketplace/enums';
import type { RecapItem, StateAbbreviation } from '@repo/supergraph/marketplace/types';
import { SafeLinkWithFallback } from '@repo/ui/SafeLinkWithFallback';
import { filterEntitiesByState } from '@repo/ui/utils/geo-data';

export type RecapListProps = {
  title: string;
  marketplaceProductType?: string;
  recapItems: {
    recapDescription?: string;
    marketplaceEntity: {
      id: string;
      name: string;
      reviewLink?: string;
      displayName?: string;
      monetizable?: MonetizableEnum;
    };
  }[];
  state?: StateAbbreviation;
  headingComponent?: React.ElementType;
};

export default function RecapListSection({
  title,
  recapItems,
  state,
  marketplaceProductType = '',
  headingComponent = 'h3',
}: RecapListProps): React.JSX.Element {
  const geoTargetedRecap = state ? filterEntitiesByState<RecapItem>(recapItems, state) : recapItems;

  const fireEventAction = usePageAction({
    actionName: 'nw_review_redirected',
    actionSection: marketplaceProductType.toLowerCase(),
    actionType: 'select',
  });

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (!marketplaceProductType) return;
    const targetLink = event.currentTarget.getAttribute('href');
    fireEventAction({
      ...(targetLink && { action_destination_url: targetLink }),
    });
  };

  return (
    <Box id="recap-section" data-testid="recap-list-section">
      <Typography variant="text">To recap our selections...</Typography>
      <Typography
        id="recap"
        variant="textLargeBold"
        component={headingComponent}
        sx={{
          fontWeight: 700,
        }}
      >
        {title}
      </Typography>
      <List sx={{ listStyleType: 'disc', paddingLeft: 3, paddingY: 0 }}>
        {geoTargetedRecap.map(({ recapDescription, marketplaceEntity }) => {
          const { id, reviewLink, displayName } = marketplaceEntity;
          return (
            <ListItem
              key={`recap-list-item-${id}`}
              sx={{ display: 'list-item', paddingY: '4px', paddingX: 0 }}
            >
              <SafeLinkWithFallback onClick={handleClick} href={reviewLink}>
                {displayName}
              </SafeLinkWithFallback>
              {recapDescription && `: ${recapDescription}`}
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
}
